import * as a from '@mitranim/js/all.mjs'
import * as ua  from './util-auth.mjs'
import * as um  from './util-misc.mjs'
import * as ut  from './util-text.mjs'
import * as ufe from './util-feed.mjs'
import * as ue  from './util-err.mjs'
import * as uc  from './util-coll.mjs'
import * as uf  from './util-form.mjs'
import * as ud  from './util-dom.mjs'
import * as c   from './conf.mjs'
import * as dm  from './dat-model.mjs'

const freeze = Object.freeze

export const selectedItemParams          = freeze({limit: c.SELECTED_ITEMS_SIZE, orderings: `reaction desc`})
export const selectedItemServices        = freeze({limit: c.SELECTED_ITEMS_SIZE, orderings: `reviewCount desc`})
export const shortParams                 = freeze({limit: c.FEED_SIZE_MEDIUM})
export const relatedParams               = freeze({...shortParams, randomOrder: true})
export const relatedParamsPersonItems    = freeze({...shortParams, randomOrder: true, ratingMin: 1})
export const recommendedParams           = freeze({limit: c.FEED_SIZE_SHORT, orderings: `reaction desc`})
export const recommendedParamsMediumSize = freeze({limit: c.FEED_SIZE_MEDIUM, orderings: [`reviewCount desc`, `publicServiceCount desc`]})
export const latestParams                = freeze({preferLatest: true})
export const latestPrivateParams         = freeze({...latestParams, includeHidden: true})
export const bukSetEnable                = freeze({isEnabled: true})
export const bukSetDisable               = freeze({isEnabled: false})
export const entParamsMini               = freeze({includeExt: false, includeAgg: false})
export const entParamsMiniPrivate        = freeze({...entParamsMini, ...latestPrivateParams})
export const recommendedParamsPubItems   = freeze({...shortParams, orderings: [`progressCache desc`, `reviewCount desc`, `publicServiceCount desc`]})

export function serviceFeedParamsSession(val) {
  return {...a.laxDict(val), nature: c.SERVICE_NATURE_SESSION}
}

export function serviceFeedParamsEvent(val) {
  return {...a.laxDict(val), nature: c.SERVICE_NATURE_EVENT}
}

export function serviceFeedParamsVideo(val) {
  return {...a.laxDict(val), nature: c.SERVICE_NATURE_VIDEO}
}

export function serviceFeedParamsBook(val) {
  return {...a.laxDict(val), nature: c.SERVICE_NATURE_BOOK}
}

export function serviceFeedParamsCourse(val) {
  return {...a.laxDict(val), nature: c.SERVICE_NATURE_COURSE}
}

/*
Uses two sources:

  * URL query.
  * Route URL captures.

Should be used by various feed routes. Query keys and names of route URL
captures must be consistent.

Slug-based routes are preferred. To avoid URL breakage, we also maintain old
routes with integer ids and UUIDs. The server supports all of these filters,
combining them. We simply send everything.
*/
export function pageCatParams(ctx) {
  const fromQuery = key => ctx.url.query.getAll(key)

  return uc.trimDict({
    considerSubcategories: true,
    mainCategoryIds: fromQuery(`concernCategoryIds`),
    concernCategoryIds: fromQuery(`mainCategoryIds`),
    mainCategoryIntIds: uc.vacOf(ctx.params.catIntId),
    mainCategorySlugs: a.vac(a.concat(
      fromQuery(`mainCategorySlugs`),
      uc.vacOf(ctx.params.catSlug),
    )),
    concernCategorySlugs: fromQuery(`concernCategorySlugs`),
  })
}

export function pageCommonParams(ctx) {
  return {...pageCatParams(ctx), ...ctx.url.query.toDictAll()}
}

export function pageCommonParamsForPersons(ctx) {
  const src = pageCommonParams(ctx)
  return {
    ...src,
    orderings: a.append(src.orderings, ...recommendedParamsPubItems.orderings),
  }
}

export function pageCommonParamsForServices(ctx) {
  const src = pageCommonParams(ctx)
  return {personIncludeBlacklisted: true, ...src}
}

export function pageCommonParamsForServicesShort(ctx) {
  return {
    ...pageCommonParamsForServices(ctx),
    ...shortParams,
    natures: [c.SERVICE_NATURE_SESSION]
  }
}

export function pageCommonParamsForEventsShort(ctx) {
  return {
    ...pageCommonParamsForServices(ctx),
    ...shortParams,
    natures: [c.SERVICE_NATURE_EVENT]
  }
}

export function pageCommonParamsForVideosShort(ctx) {
  return {
    ...pageCommonParamsForServices(ctx),
    ...shortParams,
    natures: [c.SERVICE_NATURE_VIDEO]
  }
}

export function pageCommonParamsForArticles(ctx) {
  return {
    personIncludeBlacklisted: true,
    includeBlacklisted: true,
    ...pageCommonParams(ctx),
  }
}

export function pageCommonParamsForArticlesShort(ctx) {
  return {
    ...pageCommonParamsForArticles(ctx),
    ...shortParams
  }
}

export function paramsRelArticlePage({randomOrder, personId, considerSubcategories = true, ...rest} = {}) {
  return {randomOrder, personId, considerSubcategories, ...rest}
}

export async function fetchArticle(ctx, slug) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/articles`, slug).setQuery({includeHidden: true}),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function createArticle(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/articles`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function updateArticle(ctx, {id, ...body}) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/articles`, id),
    method: a.PATCH,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function hideArticle(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/articles`, id, `hide`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

export async function showArticle(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/articles`, id, `show`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

export async function deleteArticle(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/articles`, id),
    method: a.DELETE,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchArticleSlugBySlugFuzzy(ctx, slug) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/articles`, slug, `slug`),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function cancelDiscountPurchase(ctx, subscriptionId) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/discount_purchases`, subscriptionId, `cancel`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

export async function cancelPromotionWarePurchase(ctx, promotionWareId) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/promotion_ware_purchases`, promotionWareId, `cancel`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

export async function sendArticleToModeration(ctx, id) {
  a.reqPk(id)
  return moderationSubjectToArticle(await upsertModerationPending(ctx, {articleId: id}))
}

/*
Note: the backend will usually disallow this. This function is kept in case we
want to re-enable self-publishing at some point.
*/
export async function publishArticle(ctx, id) {
  a.reqPk(id)
  return moderationSubjectToArticle(await upsertModerationApproved(ctx, {articleId: id}))
}

/*
Note: this retracts a SPECIFIC version of the article from moderation. If the
article has previous versions that are published, they stay published. This is
useful for retracting an article from an ongoing moderation process. For hiding
an article, see `hideArticle`.
*/
export async function unpublishArticle(ctx, id) {
  a.reqPk(id)
  return moderationSubjectToArticle(await upsertModerationRetracted(ctx, {articleId: id}))
}

function moderationSubjectToArticle(val) {
  return a.toInstOpt(a.optStruct(val)?.article, dm.Article)
}

export async function upsertModerationApproved(ctx, body) {
  return upsertModeration(ctx, {...a.laxDict(body), status: c.MODERATION_STATUS_APPROVED})
}

export async function upsertModerationPending(ctx, body) {
  return upsertModeration(ctx, {...a.laxDict(body), status: c.MODERATION_STATUS_PENDING})
}

export async function upsertModerationRetracted(ctx, body) {
  return upsertModeration(ctx, {...a.laxDict(body), status: c.MODERATION_STATUS_RETRACTED})
}

export async function upsertModeration(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/moderations/by_subject`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function fetchCategories(ctx) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/categories`,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchLanguages(ctx) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/languages`,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

// Why anonymous, rather than authed?
export async function subscribeByEmail(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/news_lists`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function fetchVidFeedPage(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/vids`).setQuery(body),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function vidFeedFetchFirstPage(ctx, feed) {
  return feedV2FetchFirstPage(ctx, feed, fetchVidFeedPage)
}

export async function vidFeedFetchNextPage(ctx, feed) {
  return feedV2FetchNextPage(ctx, feed, fetchVidFeedPage)
}

export async function fetchVidLatest(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/vids`, id),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function createVid(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/vids`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function updateVid(ctx, {id, ...body}) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/vids`, id),
    method: a.PATCH,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

function serviceUrl(...src) {return ut.url(`/api/v2/services`, ...src)}

export async function fetchServiceLatestPublic(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(id),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchServiceLatestPublicMini(ctx, id) {
  return new dm.Service(
    await (await a.resOk(ctx.fetch({
      url: serviceUrl(id).setQuery(entParamsMini),
      headers: [a.HEADER_JSON_ACCEPT],
    }))).json()
  )
}

export async function fetchServiceLatestMini(ctx, id) {
  return new dm.Service(
    await (await a.resOk(ctx.fetch({
      url: serviceUrl(id).setQuery(entParamsMiniPrivate),
      headers: [a.HEADER_JSON_ACCEPT],
    }))).json()
  )
}

export async function fetchServiceLatest(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(id).setQuery(latestPrivateParams),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchServiceVersion(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(id, `version`),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function createService(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function updateService(ctx, {id, ...body}) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(id),
    method: a.PATCH,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function hideService(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(id, `hide`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

export async function showService(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(id, `show`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

export async function deleteService(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(id),
    method: a.DELETE,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchServiceTimetable(ctx, {id, ...query}) {
  return (await a.resOk(ctx.fetch({
    url: serviceUrl(id, `timetable`).setQuery(query),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchServiceFiltersFormSpec(ctx, body) {
  let spec = await (await a.resOk(ctx.fetch({
    url: serviceUrl(`filters`).setQuery(body),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()

  // This is done on the frontend because our frontend artificially segregates
  // feeds by "nature", while the backend does not. We MIGHT move this to the
  // backend later.
  spec = a.reject(spec, val => val.name === `nature`)

  return uf.serverFieldSpecsToLegacyFormSpec(spec)
}

export async function fetchArticleFiltersFormSpec(ctx, body) {
  return uf.serverFieldSpecsToLegacyFormSpec(
    await (await a.resOk(ctx.fetch({
      url: ut.url(`/api/v2/articles/filters`).setQuery(body),
      headers: [a.HEADER_JSON_ACCEPT],
    }))).json(),
  )
}

export async function fetchPersonsFiltersFormSpec(ctx, body) {
  return {fields: await fetchPersonsFiltersFieldSpecs(ctx, body)}
}

export async function fetchPersonsFiltersFieldSpecs(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons/filters`).setQuery(body),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchExpertiseSlugsForPersons(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons/expertise_slugs`).setQuery(body),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

const SESSION_REVIEW_FORM_SPEC_PATH = `/api/v2/reviews/forms/cal_abs_timespans`

export async function fetchBuyerSessionReviewFormSpec(ctx, id) {
  return uf.serverFieldSpecsToLegacyFormSpec(
    await (await a.resOk(ctx.fetch({
      url: ut.url(SESSION_REVIEW_FORM_SPEC_PATH, id, `buyer`),
      headers: [a.HEADER_JSON_ACCEPT],
    }))).json()
  )
}

export async function fetchSellerSessionReviewFormSpec(ctx, id) {
  return uf.serverFieldSpecsToLegacyFormSpec(
    await (await a.resOk(ctx.fetch({
      url: ut.url(SESSION_REVIEW_FORM_SPEC_PATH, id, `seller`),
      headers: [a.HEADER_JSON_ACCEPT],
    }))).json()
  )
}

export async function fetchPromotionWares(ctx) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/promotion_wares`).setQuery({orderings: [`promotion.ord asc`, `interval asc`]}),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchSubscriptionPage(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/discounts`).setQuery({orderings: `priceCents asc`, ...a.optDict(body)}),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchSubscriptionPurchases(ctx) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/discounts/purchases`,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchPromotionWarePurchases(ctx) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/promotion_ware_purchases`).setQuery({orderings: `createdAt desc`}),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchUserOpt(ctx) {
  if (!ua.isMaybeAuthed(ctx)) return undefined

  const inp = new dm.BukInp()
  inp.user()

  try {
    return (await fetchBuk(ctx, inp))?.get?.units?.person
  }
  catch (err) {
    if (err.status === c.HTTP_UNAUTHORIZED) return undefined
    throw err
  }
}

/*
TODO: change to buket API.
We should use buket API for both GET and PATCH.
*/
export async function updateCurrentPerson(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons/current`),
    method: a.PATCH,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

// WTF?! Move all sorting to the backend!!
function practCompareByPromotions(one, two) {
  const promo = c.SLUG_PROMOTION_FAVORITE_PRACTITIONERS
  return um.compare(one.isPaidPromoActive(promo), two.isPaidPromoActive(promo))
}

// WTF?! Move all sorting to the backend!!
export function sortPractsByPromos(val) {
  return a.sort(val, practCompareByPromotions)
}

export async function fetchPractitioners(ctx, query) {
  return um.getItems(
    await fetchPractFeedPage(ctx, {total: false, ...a.reqDict(query)})
  )
}

export async function fetchLuminaryFeedPage(ctx, query) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/luminaries`).setQuery(query),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function luminaryFeedFetchFirstPage(ctx, feed) {
  return feedV2FetchFirstPage(ctx, feed, fetchLuminaryFeedPage)
}

export async function luminaryFeedFetchNextPage(ctx, feed) {
  return feedV2FetchNextPage(ctx, feed, fetchLuminaryFeedPage)
}

export async function fetchLuminary(ctx, slug) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/luminaries`, slug),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

// TODO switch to buket API.
async function fetchPersonPage(ctx, query) {
  return (await a.resOk(ctx.fetch({url: ut.url(`/api/v2/persons`).setQuery(query)}))).json()
}

export async function fetchPractFeedPage(ctx, {
  considerSubcategories = true,
  includeBlacklisted = true,
  ...rest
}) {
  return fetchPersonPage(ctx, {
    isPractitioner: true,
    isOrganization: false,
    considerSubcategories,
    includeBlacklisted,
    ...rest,
  })
}

export async function fetchFakeOrgFeedPage(ctx, {considerSubcategories = true, ...body}) {
  return fetchPersonPage(ctx, {
    isOrganization: true,
    considerSubcategories,
    ...body,
  })
}

// TODO: Implement FAQ
export async function fetchFaqFeedPage(ctx, query) {
  return (await a.resOk(ctx.fetch({url: serviceUrl().setQuery(query)}))).json()
}

export async function faqFeedFetchFirstPage(ctx, feed) {
  return feedV2FetchFirstPage(ctx, feed, fetchFaqFeedPage)
}

export async function faqFeedFetchNextPage(ctx, feed) {
  return feedV2FetchNextPage(ctx, feed, fetchFaqFeedPage)
}

export async function fetchPractGeoFeedPage(ctx, body) {
  return fetchPractFeedPage(ctx, body)
}

// Fetches a person by its old practitioner integer ID. Eventually, we should
// migrate to always using person IDs.
export async function fetchPractitioner(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons/practitioners`, id),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchPractitionerBySlug(ctx, slug) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons/practitioners/slug`, slug),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchPersonById(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons`, id),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function createImageFile(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/files/images`,
    method: a.POST,
    headers: [a.HEADER_JSON_ACCEPT],
    body: ud.reqFormData(body),
  }))).json()
}

export async function createBookFile(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/files/books`,
    method: a.POST,
    headers: [a.HEADER_JSON_ACCEPT],
    body: ud.reqFormData(body),
  }))).json()
}

export async function createFile(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/files`,
    method: a.POST,
    body: ud.reqFormData(body),
    timeout: 0,
  }))).json()
}

export async function uploadVid(ctx, {file, id}) {
  a.reqFile(file)
  return uploadVidStart(ctx, id, file)
}

export async function uploadToVimeo(ctx, file, upload) {
  a.reqFile(file)

  const formData = new FormData()
  formData.append(`file_data`, file)

  const uploadLink = ut.url(upload.upload_link)
  uploadLink.searchParams.delete(`redirect_url`)

  return uploadVidTo(ctx, uploadLink, formData)
}

/*
Returns a map of currency codes (lowercase) to cents, e.g.:
  {"usd": 123456, "gbp": 123456}
*/
export async function fetchBalance(ctx) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/balance`,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchPaymentSubject(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/payment_subjects`, id, `meta`),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

function withMeta(src) {return {includeMeta: true, ...a.optDict(src)}}

export async function createPaymentSubjectForService(ctx, body) {
  return new dm.ExtPaymentSubjectWithMeta(await (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/bookings/services`,
    method: a.POST,
    headers: a.HEADERS_JSON_INOUT,
    body: a.jsonEncode(withMeta(body)),
  }))).json())
}

// TODO fetch with meta, like for services.
export async function createPaymentSubjectForPromotion(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/promotion_order`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

// TODO fetch with meta, like for services.
export async function createPaymentSubjectForSubscription(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/discount`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

// TODO fetch with meta, like for services.
export async function createPaymentSubjectForGiftCard(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/gift_card`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

// TODO fetch with meta, like for services.
export async function createPaymentSubjectForAccessOrder(ctx, body) {
  return new dm.ExtPaymentSubjectWithMeta(
    await (await a.resOk(ctx.fetch({
      url: `/api/v2/payment_subjects/access_order`,
      method: a.POST,
      headers: a.HEADERS_JSON_INOUT,
      body: a.jsonEncode(withMeta(body)),
    }))).json()
  )
}

export async function createPaymentSubjectForPromotionWare(ctx, body) {
  return new dm.ExtPaymentSubjectWithMeta(
    await (await a.resOk(ctx.fetch({
      url: `/api/v2/payment_subjects/promotion_ware`,
      method: a.POST,
      headers: a.HEADERS_JSON_INOUT,
      body: a.jsonEncode(withMeta(body)),
    }))).json()
  )
}

export async function createPaymentSubjectForDeposit(ctx, body) {
  return new dm.ExtPaymentSubjectWithMeta(
    await (await a.resOk(ctx.fetch({
      url: `/api/v2/payment_subjects/deposit`,
      method: a.POST,
      headers: a.HEADERS_JSON_INOUT,
      body: a.jsonEncode(withMeta(body)),
    }))).json()
  )
}

export async function rescheduleBooking(ctx, id, body) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/bookings`, id, `reschedule`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function fetchGiftCardPurchases(ctx) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/gift_cards`,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function createGiftCard(ctx, body) {
  return new dm.GiftCard(await (await a.resOk(ctx.fetch({
    url: `/api/v2/gift_cards`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json())
}

export async function fetchGiftCard(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/gift_cards`, id),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function activateGiftCard(ctx, id) {
  return a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/gift_cards`, id, `activate`),
    method: a.POST,
  }))
}

export async function createPayPalOrder(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/pay_pal_orders`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function createPayPalDirectOrder(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/pay_pal_direct_orders`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function createPayPalSubscription(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/pay_pal_subscription`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function capturePayPalOrder(ctx, externalId) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/payment_subjects/pay_pal_orders`, externalId, `capture`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

export async function capturePayPalSubscription(ctx, externalId) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/payment_subjects/pay_pal_subscription`, externalId, `capture`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

// Caution: the destructuring is not redundant; the input may be a file, but the
// request must be JSON.
export async function uploadVidStart(ctx, id, {name, size}) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/vids`, id, `upload_start`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode({name, size}),
  }))).json()
}

export async function uploadVidTo(ctx, url, body) {
  return a.resOk(ctx.fetch({url, method: a.POST, body, timeout: 0}))
}

export async function createStripePaymentIntent(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/stripe_payment_intents`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

function reqStripeResSucc(res) {
  if (!res) throw Error(`unexpected empty response from `, c.STRIPE_NAME)
  if (res.error) {
    if (res.error.message) throw Error(res.error.message)
    throw res.error
  }
}

export function unpackStripePaymentIntentResponse(res) {
  reqStripeResSucc(res)

  const {paymentIntent} = res
  if (!paymentIntent) {
    throw Error(`expected `, c.STRIPE_NAME, ` response to contain "paymentIntent", got ${a.show(res)}`)
  }

  validateStripePaymentIntent(paymentIntent)
  return paymentIntent
}

export function unpackStripeSetupIntentResponse(res) {
  reqStripeResSucc(res)

  const {setupIntent} = res
  if (!setupIntent) {
    throw Error(`expected `, c.STRIPE_NAME, ` response to contain "setupIntent", got ${a.show(res)}`)
  }

  validateStripeSetupIntent(setupIntent)
  return setupIntent
}

// Slightly more descriptive error message compared to `a.req`, because
// this might be seen by users in a critical UX path.
export function validateStripePaymentIntent(val) {
  if (isStripePaymentIntent(val)) return
  throw TypeError(`expected `, c.STRIPE_NAME, ` payment intent, got ${a.show(val)}`)
}

export function validateStripeSetupIntent(val) {
  if (isStripeSetupIntent(val)) return
  throw TypeError(`expected `, c.STRIPE_NAME, ` setup intent, got ${a.show(val)}`)
}

function isStripePaymentIntent(val) {
  return a.isStruct(val) && a.isPk(val.id) && val.object === `payment_intent`
}

function isStripeSetupIntent(val) {
  return a.isStruct(val) && a.isPk(val.id) && val.object === `setup_intent`
}

export async function createStripeSubscriptionPaymentIntent(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/stripe_subscription`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function captureStripePaymentIntent(ctx, externalId) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/payment_subjects/stripe_payment_intents`, externalId, `capture`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))).json()
}

export async function captureStripePaymentIntentResponse(ctx, res) {
  res = await unpackStripePaymentIntentResponse(res)
  return captureStripePaymentIntent(ctx, res.id)
}

export async function captureStripeSubscriptionPaymentIntent(ctx, externalId, body) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/payment_subjects/stripe_subscription`, externalId, `capture`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function captureInternalPayment(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/internal/capture`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function captureFreePayment(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/payment_subjects/free/capture`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

/*
Unused for now.

export async function fetchBooking(ctx, id) {
  return (await a.resOk(await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/bookings`, id),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}
*/

export async function cancelBooking(ctx, id) {
  return a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/bookings`, id, `cancel`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(),
  }))
}

/*
This should be used for all feed endpoints of the v2 backend. They should be
implemented consistently.
*/
async function feedV2FetchFirstPage(ctx, feed, fetchPage) {
  a.req(feed, ufe.isFeed)
  return feed.withFirstPage(await fetchPage(ctx, feed.params))
}

/*
This should be used for all feed endpoints of the v2 backend. They should be
implemented consistently.
*/
async function feedV2FetchNextPage(ctx, feed, fetchPage) {
  a.req(feed, ufe.isFeed)
  a.reqFun(fetchPage)

  if (!feed.hasPage()) return fetchPage(ctx, feed)
  if (!feed.hasNext()) return feed

  return feed.withNextPage(await fetchPage(ctx, feed.nextPageParams()))
}

/*
type ExtTimezone struct {
  Timezone
  UtcOffset int64 `json:"utcOffset" db:"-"`
}

type Timezone struct {
  Id           Uuid         `json:"id"           db:"id"`
  Name         TimezoneName `json:"name"         db:"name"`
  Abbreviation string       `json:"abbreviation" db:"abbreviation"`
  Timed
}
*/
export async function fetchTimezones(ctx) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/timezones`,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchTimezoneForLocation(ctx, geoPoint, secs) {
  const {lat, lng} = a.req(geoPoint, um.isGeoPoint)
  a.reqNat(secs)

  if (!c.MAP_API_KEY) throw Error(`MAP_API_KEY not found`)

  return (await a.resOk(ctx.fetch({
    url: ut.url(c.GOOGLE_TIME_ZONE_URL).setQuery({
      key: c.MAP_API_KEY,
      coords: a.reqFin(lat) + `,` + a.reqFin(lng),
      timestamp: secs,
    }),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchCalRelTimetable(ctx) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/cal_rel_timetable`,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json().then(um.addSecretRandomKeys)
}

export async function updateCalRelTimetable(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/cal_rel_timetable`,
    method: a.POST,
    headers: a.HEADERS_JSON_INOUT,
    body: a.jsonEncode(body),
  }))).json().then(um.addSecretRandomKeys)
}

export async function fetchCalAbsTimespan(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/cal_abs_timespans`, id),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchCalAbsTimespans(ctx, query) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/cal_abs_timespans`).setQuery(query),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function createCalAbsTimetable(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/cal_abs_timespans`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function updateCalAbsTimetable(ctx, {id, ...body}) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/cal_abs_timespans`, id),
    method: a.PATCH,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

/*
Note: this intentionally doesn't send the confirmation code, in order to allow
the server to validate permissions for confirming the timespan. The confirmation
code allows to bypass permissions, which is implemented only for GET requests
used via email links. Here, we want additional validation.
*/
export async function confirmCalAbsTimespan(ctx, id) {
  return a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/cal_abs_timespans`, id, `confirm`),
    method: a.POST,
  }))
}

export async function fetchDailyMeetingToken(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/daily/cal_abs_timespan`, id, `meeting_tokens`),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchReviewFeedPage(ctx, query) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/reviews`).setQuery(query),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function reviewFeedFetchFirstPage(ctx, feed) {
  return feedV2FetchFirstPage(ctx, feed, fetchReviewFeedPage)
}

export async function reviewFeedFetchNextPage(ctx, feed) {
  return feedV2FetchNextPage(ctx, feed, fetchReviewFeedPage)
}

export async function fetchReviewBySubject(ctx, query) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/reviews/by_subject`).setQuery(query),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export function reviewFeedStats(feed) {
  return a.laxStruct(a.laxStruct(feed).lastPage()).reviewStats
}

export async function fetchReviewBySubjectOpt(ctx, query) {
  if (a.isEmpty(query)) return undefined

  try {
    return await fetchReviewBySubject(ctx, query)
  }
  catch (err) {
    if (ue.isErrNotFound(err)) return undefined
    throw err
  }
}

export async function upsertReview(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/reviews`,
    method: a.POST,
    headers: a.HEADERS_JSON_INOUT,
    body: a.jsonEncode({withResult: true, ...a.optDict(body)}),
  }))).json()
}

export async function upsertQuizResult(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/quiz/results`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function fetchMsgFeedPage(ctx, query) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/msgs`).setQuery(query),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function msgFeedFetchFirstPage(ctx, feed) {
  return feedV2FetchFirstPage(ctx, feed, fetchMsgFeedPage)
}

export async function msgFeedFetchNextPage(ctx, feed) {
  return feedV2FetchNextPage(ctx, feed, fetchMsgFeedPage)
}

export async function createMsg(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/msgs`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function updateMsg(ctx, {id, ...body}) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/msgs`, id),
    method: a.PATCH,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function fetchEnum(ctx, name) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/enums`, name),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export function paramsRelServicePage({considerSubcategories = true, ...rest}) {
  return {considerSubcategories, ...rest}
}

// WTB better naming convention.
export async function authInit(ctx, body) {
  return a.resOk(ctx.fetch({
    url: `/api/v2/auth/init`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))
}

// WTB better naming convention.
export async function authLogin(ctx, body) {
  const res = await a.resOk(authLoginRes(ctx, body))
  if (!ua.isMaybeAuthed(ctx)) {
    throw Error(`authentication pretended to work, but session credentials could not be found`)
  }
  return res
}

export async function authLoginRes(ctx, body) {
  return ctx.fetch({
    url: `/api/v2/auth/login`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  })
}

export async function fetchCurrentStripeAccount(ctx, query) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons/current/stripe_account`).setQuery(query),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchCurrentStripeConnectUrl(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: c.API_PATH_STRIPE_CONNECT_URL,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))).json()
}

export async function createInvite(ctx, body) {
  return a.resOk(ctx.fetch({
    url: `/api/v2/invites`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))
}

export async function fetchSiteFeedLinks(ctx) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/feeds/links`,
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}

export async function fetchSessionBySession(ctx, sessionId) {
  const res = await (await a.resOk(ctx.fetch({
    url: `/api/v2/auth/login_by_session`,
    method: a.POST,
    headers: a.HEADERS_JSON_INOUT,
    body: a.jsonEncode({sessionId}),
  }))).json()

  return a.laxDict(res)[c.COOKIE_NAME_SESSION]
}

// Fds = Firebase Dynamic Link
export async function fetchFds(ctx, body) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(c.FIREBASE_API_URL).setQuery({key: c.FIREBASE_WEB_API_KEY}),
    method: a.POST,
    headers: a.HEADERS_JSON_INOUT,
    body: a.jsonEncode({
      dynamicLinkInfo: {
        domainUriPrefix: c.MOBILE_DOMAIN_URI_PREFIX,
        link: ut.url(c.SERVER_ORIGIN).setQuery(body),
        androidInfo: {androidPackageName: c.ANDROID_PACKAGE_NAME},
        iosInfo: {iosBundleId: c.IOS_PACKAGE_NAME, iosAppStoreId: c.IOS_APP_STORE_ID}
      }
    }),
  }))).json()
}

/*
Unused, disabled for now.

export function buildPaypalConnectUrl() {
  a.reqValidStr(c.PAY_PAL_CONNECT_URL)

  return ut.url(c.PAY_PAL_CONNECT_URL).setQuery({
    flowEntry: `static`,
    client_id: c.PAY_PAL_CLIENT_ID,
    scopes: `openid`,
    redirect_uri: c.PAY_PAL_CONNECT_ACCOUNT_CALLBACK_URL,
  })
}
*/

export async function upsertEmailSubscription(ctx, body) {
  return a.resOk(ctx.fetch({
    url: `/api/v2/email_subscription`,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))
}

// See comment on `c.CONTENT_TYPE_JSON_QUERY`.
export function jsonQuery(src) {
  return {
    ...ut.queryToDictAll(src),
    contentType: [c.CONTENT_TYPE_JSON_QUERY],
  }
}

/*
Note: we make this request separately in order to catch and specially handle
Oauth unauthenticated / unauthorized errors.
*/
export async function fetchFacebookAccounts(ctx) {
  return (await ctx.pageDat.bukFetch({
    get: {sets: {facebookAccounts: bukSetEnable}},
  })).get.sets?.facebookAccounts
}

/*
Note: we make this request separately in order to catch and specially handle
Oauth unauthenticated / unauthorized errors.
*/
export async function fetchInstagramBusinessAccounts(ctx) {
  return (await ctx.pageDat.bukFetch({
    get: {sets: {instagramBusinessAccounts: bukSetEnable}},
  })).get.sets?.instagramBusinessAccounts
}

// Deprecated, TODO drop.
export async function createMetaData(ctx, body) {
  return a.resOk(ctx.fetch({
    url: `/api/v2/meta_datas`,
    keepalive: true,
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))
}

export async function personDelete(ctx, id) {
  return a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons`, id),
    method: a.DELETE,
  }))
}

export async function personUndelete(ctx, id) {
  return a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/persons`, id, `undelete`),
    method: a.POST,
  }))
}

export async function quizFunnel(ctx, body) {
  return a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/quiz/funnel`),
    method: a.POST,
    headers: [a.HEADER_JSON],
    body: a.jsonEncode(body),
  }))
}

export async function fetchBuk(ctx, inp) {
  return (await a.resOk(ctx.fetch(bukReqOpt(inp)))).json()
}

export function bukReqOpt(body) {
  return {
    url: `/api/v2/buk`,
    method: a.POST,
    headers: a.HEADERS_JSON_INOUT,
    body: a.jsonEncode(bukInpNorm(body)),
  }
}

/*
Ensures that our client always avoids auto-enabling sets in the buket API.
Auto-enabling is now deprecated. For backwards compatibility with mobile
clients, our backend auto-enables sets by default. Clients must opt out by
explicitly providing `autoEnableSets: false`. However, this special case is
very easy to forget. This function ensures that we always use the new behavior,
for all buket inputs.
*/
export function bukInpNorm(val) {
  val = a.laxStruct(val)
  val.get ??= {}
  val.get.meta ??= {}
  val.get.meta.autoEnableSets = false
  return val
}

export async function createAct(ctx, body) {
  if (!c.ENABLE_ACTS) return undefined
  const opt = bukReqOpt({act: {acts: {create: [a.reqStruct(body)]}}})
  opt.keepalive = true
  const buk = await (await a.resOk(ctx.fetch(opt))).json()
  return a.head(buk?.act?.acts?.create)
}

/*
Only for requests-responses that use structured data.
Text prompts with text responses should use `aiAskStream`.
*/
export async function aiAsk(ctx, src) {
  return (await a.resOk(ctx.fetch({
    url: `/api/v2/ai/ask`,
    method: a.POST,
    headers: a.HEADERS_JSON_INOUT,
    body: a.jsonEncode(src),
  }))).json()
}

/*
The response is streamed as JSON lines. Callers should use `streamLines`, use
`for await` to iterate, and parse lines if they want.
*/
export async function aiAskStream(ctx, src) {
  return a.resOk(ctx.fetch({
    url: `/api/v2/ai/ask/stream`,
    method: a.POST,
    headers: [
      a.HEADER_JSON,
      c.HEADER_ACCEPT_JSON_LINES,
    ],
    body: a.jsonEncode(src),
  }))
}

export async function aiConvo(ctx, id) {
  return (await a.resOk(ctx.fetch({
    url: ut.url(`/api/v2/ai/convo`, id),
    headers: [a.HEADER_JSON_ACCEPT],
  }))).json()
}
